import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { HorizontalScrollSection, ScrollCard } from '@/components/HorizontalScrollSection';
import { Skeleton } from '@/components/ui/skeleton';
import { Layers } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  listing_type: 'event' | 'experience';
}

interface CategoryCarouselProps {
  type: 'event' | 'experience';
  title?: string;
  className?: string;
}

export const CategoryCarousel = ({ type, title, className }: CategoryCarouselProps) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, [type]);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('listing_type', type)
      .eq('is_active', true)
      .eq('page_enabled', true)
      .order('display_order');

    if (!error && data) {
      setCategories(data as Category[]);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={className}>
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="flex gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[45%] sm:w-[31%] lg:w-[24%]">
              <Skeleton className="aspect-[4/3] rounded-xl mb-3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return null;
  }

  const defaultTitle = type === 'event' ? 'Browse Event Categories' : 'Browse Experience Categories';

  return (
    <HorizontalScrollSection
      title={title || defaultTitle}
      titleIcon={<Layers className="h-5 w-5 text-muted-foreground" />}
      className={className}
    >
      {categories.map((category) => (
        <ScrollCard key={category.id}>
          <Link to={`/category/${category.slug}`} className="block group">
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden mb-3">
              <img
                src={category.image_url || 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=800&h=600&fit=crop'}
                alt={category.name}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-bold text-lg md:text-xl">{category.name}</h3>
              </div>
            </div>
          </Link>
        </ScrollCard>
      ))}
    </HorizontalScrollSection>
  );
};
